/**
 * Scoring of a round – the same numbers on the wall, the phone and the server (highscore).
 *
 * The player collects gold coins (own contribution to the AV depot). The state subsidy
 * ("Grundzulage") follows the rule of the Altersvorsorgedepot: 50 % on the first 360 € own
 * contribution, 25 % on the rest up to 1.800 € – at most 540 € a year. In the game the bonus
 * coins are pulled to the player by themselves, like the subsidy is paid automatically.
 * On top of it two special coins come once a round each (SPECIAL_COINS) – they count to the subsidy too.
 */

/** gold coins in one round – all collected = the maximum own contribution */
export const TOTAL_COINS = 36;
/** value of one gold coin (€) */
export const COIN_EUR = 50;
/** 1:2 – subsidy per euro on the first 360 € */
export const SUBSIDY_HIGH_RATE = 0.5;
export const SUBSIDY_HIGH_LIMIT_EUR = 360;
/** 1:4 – subsidy per euro above that, up to the maximum own contribution */
export const SUBSIDY_LOW_RATE = 0.25;
export const MAX_OWN_EUR = TOTAL_COINS * COIN_EUR;
/** subsidy levels the game celebrates (plane with banner) */
export const SUBSIDY_CHECKPOINTS_EUR = [180, 360, 540] as const;

/** a bonus coin brings the subsidy in steps of this much – the rest comes with the last one */
export const BONUS_STEP_EUR = 50;

/** Grundzulage (€) for an own contribution (€). */
export function subsidyEur(ownEur: number): number {
  const own = Math.min(Math.max(0, ownEur), MAX_OWN_EUR);
  const high = Math.min(own, SUBSIDY_HIGH_LIMIT_EUR);
  return high * SUBSIDY_HIGH_RATE + (own - high) * SUBSIDY_LOW_RATE;
}

/** highest Grundzulage (540 €) */
export const MAX_BASE_SUBSIDY_EUR = subsidyEur(MAX_OWN_EUR);

/**
 * Grundzulage (€) paid out by bonus coins once `gold` gold coins are collected: the earned subsidy in full steps of
 * 50 € – with all gold coins the whole of it (the last 40 €, a rest of the change of the rate, come at the very end).
 */
export function bonusDueEur(gold: number): number {
  const earned = subsidyEur(gold * COIN_EUR);
  return gold >= TOTAL_COINS ? earned : Math.floor(earned / BONUS_STEP_EUR) * BONUS_STEP_EUR;
}

/**
 * Bonus coins (Grundzulage) are earned by the gold coins: each one brings 50 € – about every 2nd gold coin while the
 * own contribution is below 360 € (1:2), then every 4th (1:4): at 2, 4, 6, 9, 13, 17 … 33 coins. The last one, with
 * the 36th gold coin, brings the remaining 40 €. 11 bonus coins, 540 € in all.
 */
export const BONUS_COIN_AFTER: readonly number[] = (() => {
  const after: number[] = [];
  let paid = 0;
  for (let n = 1; n <= TOTAL_COINS; n++) {
    if (bonusDueEur(n) <= paid) continue;
    paid = bonusDueEur(n);
    after.push(n);
  }
  return after;
})();

export type SpecialCoinId = 'starter' | 'baby';

export interface SpecialCoin {
  id: SpecialCoinId;
  /** shown in the game when it is collected */
  name: string;
  /** subsidy it brings (€) */
  eur: number;
}

/**
 * Special coins: each one comes once a round and has to be collected (flown through) like a gold coin.
 * Starter-Boost = Berufseinsteigerbonus (early in life), Baby-Bonus = Kinderzulage (in the middle of it).
 * They count to the state subsidy, a hit does not cost them. When they come: TUNING.special (game-core).
 */
export const SPECIAL_COINS: readonly SpecialCoin[] = [
  { id: 'starter', name: 'Starter-Boost', eur: 200 },
  { id: 'baby', name: 'Baby-Bonus', eur: 300 },
];

/** highest state subsidy of a round: Grundzulage + all special coins (1.040 €) */
export const MAX_SUBSIDY_EUR = MAX_BASE_SUBSIDY_EUR + SPECIAL_COINS.reduce((sum, c) => sum + c.eur, 0);

/** The known special coins in `ids` (each once, in the order of SPECIAL_COINS) – also from a comma-separated string. */
export function specialsOf(ids: readonly string[] | string | undefined): SpecialCoinId[] {
  const list = typeof ids === 'string' ? ids.split(',') : (ids ?? []);
  return SPECIAL_COINS.filter((c) => list.includes(c.id)).map((c) => c.id);
}

export interface Score {
  coins: number;
  /** AV depot: own contribution (€) */
  ownEur: number;
  /** state subsidy (€): Grundzulage + special coins */
  subsidyEur: number;
  /** depot + subsidy (€) – ranks the highscore */
  totalEur: number;
  /** special coins collected */
  specials: SpecialCoinId[];
  /** every coin collected (special coins too), nothing lost */
  max: boolean;
}

export function scoreOf(coins: number, specials: readonly string[] | string = []): Score {
  const c = Math.min(Math.max(0, Math.floor(coins)), TOTAL_COINS);
  const ownEur = c * COIN_EUR;
  const got = specialsOf(specials);
  const sub = subsidyEur(ownEur) + SPECIAL_COINS.reduce((sum, s) => sum + (got.includes(s.id) ? s.eur : 0), 0);
  return { coins: c, ownEur, subsidyEur: sub, totalEur: ownEur + sub, specials: got, max: c === TOTAL_COINS && got.length === SPECIAL_COINS.length };
}

/** Euro amount in German notation: 1860 → "1.860 €", 12.5 → "12,50 €". */
export function formatEuro(value: number): string {
  const cents = Math.round(Math.abs(value) * 100);
  const whole = String(Math.floor(cents / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const frac = cents % 100 ? `,${String(cents % 100).padStart(2, '0')}` : '';
  return `${value < 0 ? '−' : ''}${whole}${frac} €`;
}
